import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface OrderHistoryItem {
  id: number;
  total: number;
  /**
   * The backend's status, as last read. 'processing' when placed; refreshed from the server
   * while the order is on screen (see OrderDetailsComponent.refreshRecentStatuses), so a
   * reload still shows what was last known rather than going back to "waiting".
   */
  status: string;
  items: any[];
  timestamp: number;
  note?: string;

  /*
   * What a promo code took off this order, and which code did it.
   *
   * ⚠️ OPTIONAL, and must stay that way: entries written before promo codes existed are
   * already sitting in sessionStorage and are read back as-is. Requiring these would not
   * fail loudly — it would just be a lie the compiler believes, and `discountTotal`
   * would be `undefined` at runtime on every one of them. iOS made the same two fields
   * optional in AwaitingPaymentStore for this reason.
   *
   * `total` remains the NET figure, as the server reports it. The discount explains it;
   * it does not modify it.
   */
  discountTotal?: number;
  promoCode?: string;

  /** The tip included in `total`, if any. Optional for the same reason as the two above. */
  tipAmount?: number;

  /** The delivery fee included in `total`: absent for none, 0 for free delivery. */
  deliveryFee?: number;

  /**
   * The venue the order was placed at. History is one list for every venue the browser has
   * ordered from, so this is what keeps venue B's screen from listing venue A's orders.
   * Optional: entries written before it existed carry none, and are shown wherever the guest is,
   * as they always were.
   */
  shopId?: number;
}

/**
 * How long a submitted order stays worth offering the guest a way back to.
 *
 * Two hours is iOS's window (`OrderStorage.getAllOrdersWithinTwoHours`), and the two
 * must agree: the same guest can have the app and the web menu open on one table.
 */
export const RECENT_ORDER_WINDOW_MS = 2 * 60 * 60 * 1000;

/**
 * The orders recent enough to show, newest first.
 *
 * ⚠️ Filtered on READ, never pruned on write. A record deleted because it aged out is
 * gone for good, and this window is a display rule, not a retention policy — the order
 * itself still exists, and `getOrderById` must keep finding it for anything already
 * pointing at it.
 *
 * ⚠️ `now` is a parameter, not `Date.now()` inside. Callers evaluate this per change
 * detection so a bar can disappear as its order ages, and a test needs to be able to
 * say when "now" is without waiting two hours.
 */
export function recentOrders(items: OrderHistoryItem[], now: number, shopId?: number | string | null): OrderHistoryItem[] {
  const cutoff = now - RECENT_ORDER_WINDOW_MS;
  const venue = shopId != null && shopId !== '' ? Number(shopId) : null;

  return items
    .filter(order => typeof order?.timestamp === 'number' && order.timestamp >= cutoff)
    .filter(order => venue === null || order.shopId == null || order.shopId === venue)
    .sort((a, b) => b.timestamp - a.timestamp);
}

@Injectable({
  providedIn: 'root'
})
export class OrderHistoryService {
  private orderHistory = new BehaviorSubject<OrderHistoryItem[]>([]);
  orderHistory$ = this.orderHistory.asObservable();

  constructor() {
    this.orderHistory.next(this.load());
  }

  addOrder(order: OrderHistoryItem): void {
    this.save([order, ...this.orderHistory.getValue()]);
  }

  /**
   * Records what the server says an order's status is now. A no-op for an unknown order or an
   * unchanged status, so a refresh that learns nothing does not re-emit or rewrite storage.
   */
  setStatus(orderId: number, status: string, shopId?: number | string | null): void {
    const history = this.orderHistory.getValue();
    const order = this.getOrderById(orderId, shopId);
    if (!order || !status || order.status === status) return;
    this.save(history.map(item => (item === order ? { ...item, status } : item)));
  }

  /**
   * ⚠️ localStorage, not sessionStorage (which is where this used to live). Tapping an order
   * notification usually opens a NEW tab, and sessionStorage is per tab — that guest landed on
   * the order screen with no previous orders at all, and so no way to see the one the
   * notification was about.
   *
   * Pruned here, on load, at a day: long past the two-hour display window, so `getOrderById`
   * still finds anything recent enough to be pointed at, without the list growing for ever.
   * A tab still holding the old sessionStorage list is folded in once.
   */
  private load(): OrderHistoryItem[] {
    const cutoff = Date.now() - OrderHistoryService.RETENTION_MS;
    const stored = this.read(() => localStorage.getItem(OrderHistoryService.KEY));
    const legacy = this.read(() => sessionStorage.getItem(OrderHistoryService.KEY));
    const known = new Set(stored.map(order => order.id));
    const merged = [...stored, ...legacy.filter(order => !known.has(order.id))]
      .filter(order => typeof order?.timestamp === 'number' && order.timestamp >= cutoff);

    if (legacy.length) {
      try { sessionStorage.removeItem(OrderHistoryService.KEY); } catch { /* storage blocked */ }
      this.persist(merged);
    }
    return merged;
  }

  private save(history: OrderHistoryItem[]): void {
    this.orderHistory.next(history);
    this.persist(history);
  }

  /** Blocked or full storage keeps the in-memory list, which still serves this page load. */
  private persist(history: OrderHistoryItem[]): void {
    try {
      localStorage.setItem(OrderHistoryService.KEY, JSON.stringify(history));
    } catch { /* storage blocked or full */ }
  }

  private read(get: () => string | null): OrderHistoryItem[] {
    try {
      const parsed = JSON.parse(get() || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private static readonly KEY = 'orderHistory';
  private static readonly RETENTION_MS = 24 * 60 * 60 * 1000;

  /**
   * Pass `shopId` wherever the venue is known: order ids are only unique within one venue's store,
   * and history now spans every venue this browser ordered from.
   */
  getOrderById(orderId: number, shopId?: number | string | null): OrderHistoryItem | undefined {
    const venue = shopId != null && shopId !== '' ? Number(shopId) : null;
    return this.orderHistory.getValue().find(order =>
      order.id === orderId && (venue === null || order.shopId == null || order.shopId === venue)
    );
  }

  /**
   * ⚠️ Deliberately NOT exposed as a `map`-derived observable.
   *
   * The source is a BehaviorSubject that emits only when history CHANGES, so a derived
   * stream would hand out a list computed once and never recomputed — an order aging
   * past the window would never remove the bar from a menu left open. Callers read this
   * from a getter instead, which default change detection re-evaluates.
   */
  getRecentOrders(now: number = Date.now(), shopId?: number | string | null): OrderHistoryItem[] {
    return recentOrders(this.orderHistory.getValue(), now, shopId);
  }

  clearHistory(): void {
    this.orderHistory.next([]);
    try { localStorage.removeItem(OrderHistoryService.KEY); } catch { /* storage blocked */ }
  }
}
